<template>
  <div style="height:100%;">
    <commodity-analysis
      :Analytics="Analytics"
      :colorTheme="colorTheme"
      :loadingAll="loadingAll"
      :processData="processData"
      :renderChartSignal="renderChartSignal"
      :isPrices="isPrices"
      :idChart="idChart"
      :infoCharts="infoCharts"
      :infoLegends="infoLegends"
      :dataNest="dataNest"
      :asset="Analytics.assetType"
      @onTableFormat="onHoverChart"
    />
  </div>
</template>

<script>
import CommodityAnalysis from "./CommodityAnalysis.vue";
import { defaultAnalytics } from "@/_core/data/Analytics/AnalyticsHelpers.js";
import {
  colorSelection4,
  formatM,
  formatPercentSm
} from "@/_core/_helpers/_parametric_tools";
import { interpolateRgbBasis, scaleOrdinal, range, rgb as rgbD3 } from "d3";
import { replaceCharacters } from "../../_core/_helpers/_helpers";

export default {
  components: { CommodityAnalysis },
  name: "CommodityQtyGeneral",
  props: {
    Analytics: {
      required: true,
      default: defaultAnalytics()
    },
    colorTheme: { required: true, type: Boolean, default: true },
    loadingAll: { required: true, type: Boolean, default: false },
    processData: { required: true },
    renderChartSignal: { required: true, default: 0 },
    idChart: { required: true, default: "" },
    dataNest: { required: true, default: "" }
  },
  data() {
    return {
      infoCharts: [],
      infoLegends: [],
      isPrices: false
    };
  },
  created() {
    this.getInfoCharts();
  },
  computed: {
    isCurrency() {
      return this.Analytics.assetType === "currency";
    }
  },
  methods: {
    getInfoCharts() {
      const allUniqueKeys = [];
      const dataUnitConfig = {};

      this.Analytics["rawData"][this.dataNest].forEach(d => {
        const keys = Object.keys(d);
        keys.forEach(key => {
          if (!allUniqueKeys.includes(key)) {
            const userSelectedAsset = this.Analytics.userSelections
              .selectedAsset[0];
            if (!this.isCurrency) {
              dataUnitConfig["sUnit"] = userSelectedAsset.fk_idcurrency;
              dataUnitConfig["unit"] = userSelectedAsset.fk_idunit;
            } else {
              const currName = userSelectedAsset.currency_name;
              dataUnitConfig["sUnit"] = currName.slice(0, 3);
              dataUnitConfig["unit"] = currName.slice(3);
            }
            allUniqueKeys.push(key);
          }
        });
      });
      // === Get different scale colors
      let colorScale;
      const chartInfo = allUniqueKeys.filter(
        d => d.startsWith("q_") && !d.endsWith("Perc")
      );
      if (chartInfo.length > 1) {
        const colorInterpolator = interpolateRgbBasis(colorSelection4);
        colorScale = scaleOrdinal()
          .domain(chartInfo)
          .range(
            range(chartInfo.length).map(t =>
              colorInterpolator(t / (chartInfo.length - 1))
            )
          );
      } else {
        colorScale = scaleOrdinal()
          .domain(chartInfo)
          .range([colorSelection4[0]]);
      }
      const chartInfoArray = chartInfo.map(d => {
        const dTransformed = replaceCharacters(d);

        const labelName = d
          .replace("q_", "")
          .split(" ")
          .map(this.capitalizeWord)
          .join(" ");

        const color = colorScale(d);
        const colorWithOpacity = rgbD3(color)
          .toString()
          .replace("rgb", "rgba")
          .replace(")", ", 0.8)");

        const colorWithOpacityTable = rgbD3(color)
          .toString()
          .replace("rgb", "rgba")
          .replace(")", ", 0.25)");

        Object.assign(this.Analytics["userSelections"], {
          [`show${dTransformed}`]: true
        });
        return {
          label: labelName,
          labelTable: labelName,
          nameEvent: dTransformed.split(" ").join(""),
          elementName: dTransformed.split(" ").join(""),
          nameData: d,
          classNameIcon: d,
          colorBdr: color,
          colorBck: colorWithOpacity,
          colorBckTable: colorWithOpacityTable,
          unitPerc: `${d}Perc`,
          show: `show${dTransformed}`,
          formatF: formatM,
          formatPerc: formatPercentSm,
          isPrices: this.isPrices,
          figureElements: this.isPrices ? "bar" : "stackedBar",
          divQ: true,
          showText: true,
          typeFigure: {
            nameFigure: "multipleStackedBars",
            toZero: true
          },
          ...dataUnitConfig
        };
      });

      Object.assign(this.infoCharts, chartInfoArray);
      this.$emit("changeInfoTable", chartInfoArray ? chartInfoArray : [{}]);
      this.infoLegends = this.createInfoLegends(this.infoCharts);
    },
    createInfoLegends(infoCharts) {
      return infoCharts.map(chart => ({
        label: chart.label,
        show: chart.show,
        classNameIcon: chart.classNameIcon,
        colorBdr: chart.colorBdr,
        colorBck: chart.colorBck
      }));
    },
    capitalizeWord(word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    },
    onHoverChart(val) {
      this.$emit("onTableFormat", val);
    }
  }
};
</script>
